import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { getCesionesDistribucionDetalle } from '../api/apiConfig';
import { MdArrowBack, MdReceipt } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import * as XLSX from 'xlsx';

const DetalleCesion = () => {
  const location = useLocation();
  const cesionario = location.state?.cesionario;
  const navigate = useNavigate();
  const { user } = useUser();
  const [detalleData, setDetalleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  const formatNumber = (num) => {
    if (!num) return '$0';
    return `$${parseFloat(num).toLocaleString('es-CL')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Handle format like "2025-10-22 00:00:00"
    const datePart = dateStr.split(' ')[0]; // Remove time part
    const [year, month, day] = datePart.split('-');
    return `${day}-${month}-${year}`;
  };

  const filterData = (dataToFilter) => {
    if (!searchTerm.trim()) return dataToFilter;
    const searchLower = searchTerm.toLowerCase().trim();
    return dataToFilter.filter(item => {
      const searchableFields = [item.CESIONARIO, item.FACTORING, item.CEDENTE, item.CLIENTE_IF, item.FCH_EMIS_DTE, item.FOLIO_DOC, item.TIPO_DOC, item.FCH_CESION, item.MNT_CESION];
      return searchableFields.some(field => field != null && String(field).toLowerCase().includes(searchLower));
    });
  };

  const getSortedData = () => {
    let dataToSort = filterData(detalleData);
    if (!sortColumn) return dataToSort;
    return [...dataToSort].sort((a, b) => {
      let aValue = a[sortColumn]; let bValue = b[sortColumn];
      if (aValue == null) aValue = ''; if (bValue == null) bValue = '';
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);
      const comparison = isNumeric ? aNum - bNum : String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase(), 'es', { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const sortedData = getSortedData();

  const handleSort = (column) => {
    if (sortColumn === column) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort style={{ marginLeft: '4px', opacity: 0.3 }} />;
    return sortDirection === 'asc' ? <FaSortUp style={{ marginLeft: '4px' }} /> : <FaSortDown style={{ marginLeft: '4px' }} />;
  };

  const exportToExcel = () => {
    const dataToExport = getSortedData();
    const data = dataToExport.map(item => ({
      'Rut Factoring': item.CESIONARIO,
      'Factoring': item.FACTORING,
      'Cedente': item.CEDENTE,
      'Cliente IF': item.CLIENTE_IF,
      'Fecha Emisión': formatDate(item.FCH_EMIS_DTE),
      'Folio': item.FOLIO_DOC,
      'Tipo Doc': item.TIPO_DOC,
      'Fecha Cesión': formatDate(item.FCH_CESION),
      'Monto Cesión': item.MNT_CESION
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Detalle Cesiones');
    
    XLSX.writeFile(wb, `detalle_cesiones_${cesionario}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  useEffect(() => {
    const fetchDetalle = async () => {
      if (!user || !cesionario) return;
      
      try {
        const data = await getCesionesDistribucionDetalle(user, cesionario);
        setDetalleData(data || []);
      } catch (error) {
        console.error('Error fetching detalle:', error);
        setDetalleData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDetalle();
  }, [user, cesionario]);

  return (
    <div className="panel-container">
      <div className="panel-header">
        <div className="header-left">
          <button
            onClick={() => navigate('/inicio')}
            className="boton-volver-top"
            style={{padding: '1rem 1.5rem'}}
          >
            <MdArrowBack className="w-4 h-4 mr-2" style={{display: 'inline'}} />
            Volver
          </button>
        </div>
      </div>
      

      <div className="seccion-datos">
        <div className="flex justify-between items-center mb-4">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
            Cesiones Detalladas
            <span className="text-sm ml-2" style={{color: 'var(--if-text-muted)'}}>
              ({detalleData.length} registros)
            </span>
          </h3>
          {detalleData.length > 0 && (
            <button
              onClick={exportToExcel}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                borderLeft: '3px solid var(--if-primary)'
              }}
            >
              <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
              Exportar Excel
            </button>
          )}
        </div>
        {detalleData.length > 0 && (
          <div className="mb-4">
            <div className="relative" style={{ maxWidth: '500px' }}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IoSearchOutline className="h-5 w-5" style={{ color: '#9ca3af' }} />
              </div>
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar en todas las columnas..." className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              {searchTerm && <button type="button" onClick={() => setSearchTerm('')} className="absolute inset-y-0 right-0 pr-3 flex items-center" style={{ color: '#9ca3af' }} title="Limpiar búsqueda"><IoClose className="h-5 w-5" /></button>}
            </div>
            {searchTerm && <div className="mt-2 text-sm" style={{ color: 'var(--if-text-muted)' }}>{sortedData.length === 0 ? <span>No se encontraron resultados para &quot;{searchTerm}&quot;</span> : <span>{sortedData.length} resultado{sortedData.length !== 1 ? 's' : ''} encontrado{sortedData.length !== detalleData.length ? ` de ${detalleData.length} registros` : ''}</span>}</div>}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
          </div>
        ) : detalleData.length === 0 ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>
            No hay datos disponibles
          </div>
        ) : sortedData.length === 0 && searchTerm ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>No se encontraron resultados para &quot;{searchTerm}&quot;</div>
        ) : (
          <div className="tabla-container">
            <table>
              <thead>
                <tr>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('CESIONARIO')}>Rut Factoring {getSortIcon('CESIONARIO')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('FACTORING')}>Factoring {getSortIcon('FACTORING')}</th>
                  <th style={{minWidth: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('CEDENTE')}>Cedente {getSortIcon('CEDENTE')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('CLIENTE_IF')}>Cliente IF {getSortIcon('CLIENTE_IF')}</th>
                  <th style={{textAlign: 'center', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('FCH_EMIS_DTE')}>Fecha Emisión {getSortIcon('FCH_EMIS_DTE')}</th>
                  <th style={{textAlign: 'center', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('FOLIO_DOC')}>Folio {getSortIcon('FOLIO_DOC')}</th>
                  <th style={{textAlign: 'center', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('TIPO_DOC')}>Tipo Doc {getSortIcon('TIPO_DOC')}</th>
                  <th style={{textAlign: 'center', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('FCH_CESION')}>Fecha Cesión {getSortIcon('FCH_CESION')}</th>
                  <th style={{textAlign: 'right', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('MNT_CESION')}>Monto Cesión {getSortIcon('MNT_CESION')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.CESIONARIO}</td>
                    <td>{item.FACTORING}</td>
                    <td style={{minWidth: '100px'}}>{item.CEDENTE}</td>
                    <td>{item.CLIENTE_IF}</td>
                    <td style={{textAlign: 'center'}}>{formatDate(item.FCH_EMIS_DTE)}</td>
                    <td style={{textAlign: 'center'}}>{item.FOLIO_DOC}</td>
                    <td style={{textAlign: 'center'}}>{item.TIPO_DOC}</td>
                    <td style={{textAlign: 'center'}}>{formatDate(item.FCH_CESION)}</td>
                    <td style={{textAlign: 'right'}}>{formatNumber(item.MNT_CESION)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetalleCesion;